/**
 * Report Export Utilities
 * Handles PDF and Excel export with custom layouts
 */

import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Modern PDF generation with jsPDF 2.x + autotable
export const exportToPDF = (report, data, layout) => {
  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    let yPos = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    // If custom layout exists, render it
    if (layout && layout.components && layout.components.length > 0) {
      layout.components.forEach((component) => {
        // Check if we need a new page
        if (yPos > pageHeight - 30) {
          doc.addPage();
          yPos = 20;
        }

        switch (component.type) {
          case 'header':
            yPos = renderHeader(doc, component, yPos, pageWidth, margin);
            break;

          case 'table':
            if (data && data.data && data.data.length > 0) {
              yPos = renderTable(doc, data.data, component.config, yPos);
            }
            break;

          case 'chart':
            yPos = renderChartPlaceholder(doc, component, yPos, pageWidth, margin);
            break;

          case 'text':
            yPos = renderText(doc, component, yPos, pageWidth, margin);
            break;

          case 'summary_box':
            yPos = renderSummaryBox(doc, component, data.data, yPos, pageWidth, margin);
            break;

          case 'divider':
            yPos = renderDivider(doc, component, yPos, pageWidth, margin);
            break;

          case 'page_break':
            doc.addPage();
            yPos = 20;
            break;

          default:
            break;
        }
      });
    } else {
      // Default layout - title and table
      yPos = renderHeader(doc, {
        config: { title: report.name, subtitle: report.description }
      }, yPos, pageWidth, margin);

      if (data && data.data && data.data.length > 0) {
        yPos = renderTable(doc, data.data, { zebraStripe: true, showBorders: true }, yPos);
      }
    }

    // Footer on last page
    const finalY = doc.internal.pageSize.getHeight() - 15;
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Generated: ${new Date().toLocaleString()} | ${data.rowCount || 0} rows`, margin, finalY);

    // Save the PDF
    doc.save(`${report.name.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.pdf`);
    
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

// Render header component
const renderHeader = (doc, component, yPos, pageWidth, margin) => {
  const { title, subtitle } = component.config;

  // Draw blue accent bar
  doc.setFillColor(37, 99, 235); // blue-600
  doc.rect(margin, yPos - 3, 3, 12, 'F');

  // Title
  doc.setFontSize(20);
  doc.setTextColor(31, 41, 55); // gray-900
  doc.setFont('helvetica', 'bold');
  doc.text(title || 'Report', margin + 6, yPos + 5);

  yPos += 10;

  // Subtitle
  if (subtitle) {
    doc.setFontSize(11);
    doc.setTextColor(107, 114, 128); // gray-600
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, margin + 6, yPos);
    yPos += 6;
  }

  return yPos + 5;
};

// Render table with autotable
const renderTable = (doc, rows, config, yPos) => {
  if (!rows || rows.length === 0) return yPos;

  const headers = Object.keys(rows[0]);
  const tableData = rows.map(row => headers.map(h => formatValue(row[h])));

  doc.autoTable({
    head: [headers],
    body: tableData,
    startY: yPos,
    margin: { left: 15, right: 15 },
    styles: {
      fontSize: 9,
      cellPadding: 3
    },
    headStyles: {
      fillColor: [243, 244, 246], // gray-100
      textColor: [31, 41, 55], // gray-900
      fontStyle: 'bold'
    },
    alternateRowStyles: config.zebraStripe !== false ? {
      fillColor: [249, 250, 251] // gray-50
    } : {},
    theme: config.showBorders !== false ? 'grid' : 'plain'
  });

  return doc.lastAutoTable.finalY + 10;
};

// Render chart placeholder
const renderChartPlaceholder = (doc, component, yPos, pageWidth, margin) => {
  const { chartType = 'bar' } = component.config;
  const boxHeight = 60;

  doc.setFillColor(243, 244, 246); // gray-100
  doc.rect(margin, yPos, pageWidth - (margin * 2), boxHeight, 'F');

  doc.setFontSize(10);
  doc.setTextColor(156, 163, 175); // gray-400
  doc.text(`[${chartType.toUpperCase()} Chart - View in web for interactive chart]`, 
    pageWidth / 2, yPos + boxHeight / 2, { align: 'center' });

  return yPos + boxHeight + 10;
};

// Render text block
const renderText = (doc, component, yPos, pageWidth, margin) => {
  const { content = '' } = component.config;
  
  doc.setFontSize(10);
  doc.setTextColor(31, 41, 55); // gray-900
  doc.setFont('helvetica', 'normal');

  const lines = doc.splitTextToSize(content, pageWidth - (margin * 2));
  doc.text(lines, margin, yPos);

  return yPos + (lines.length * 5) + 5;
};

// Render summary box
const renderSummaryBox = (doc, component, rows, yPos, pageWidth, margin) => {
  const { label = 'Total', aggregate = 'sum' } = component.config;
  const value = calculateAggregate(rows, component.config);

  const boxWidth = 80;
  const boxHeight = 30;

  // Draw box with blue theme
  doc.setFillColor(239, 246, 255); // blue-50
  doc.setDrawColor(59, 130, 246); // blue-500
  doc.setLineWidth(0.5);
  doc.rect(margin, yPos, boxWidth, boxHeight, 'FD');

  // Label
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128); // gray-600
  doc.text(label, margin + 5, yPos + 8);

  // Value
  doc.setFontSize(18);
  doc.setTextColor(30, 64, 175); // blue-900
  doc.setFont('helvetica', 'bold');
  doc.text(formatValue(value), margin + 5, yPos + 20);

  return yPos + boxHeight + 10;
};

// Render divider
const renderDivider = (doc, component, yPos, pageWidth, margin) => {
  const { style = 'solid' } = component.config;

  doc.setDrawColor(209, 213, 219); // gray-300
  doc.setLineWidth(0.5);

  if (style === 'dashed') {
    doc.setLineDash([3, 3]);
  } else if (style === 'dotted') {
    doc.setLineDash([1, 2]);
  }

  doc.line(margin, yPos, pageWidth - margin, yPos);
  doc.setLineDash([]); // Reset

  return yPos + 8;
};

// Calculate aggregate value
const calculateAggregate = (rows, config) => {
  if (!rows || rows.length === 0) return 0;
  
  const { aggregate = 'count', field } = config;
  
  switch (aggregate) {
    case 'count':
      return rows.length;
    
    case 'sum':
      if (!field) return 0;
      return rows.reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0);
    
    case 'avg':
      if (!field) return 0;
      const total = rows.reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0);
      return total / rows.length;
    
    case 'min':
      if (!field) return 0;
      return Math.min(...rows.map(row => parseFloat(row[field]) || 0));
    
    case 'max':
      if (!field) return 0;
      return Math.max(...rows.map(row => parseFloat(row[field]) || 0));
    
    default:
      return 0;
  }
};

// Format value for display
const formatValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') {
    // Check if it looks like currency
    if (value > 100 || value < -100) {
      return '£' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return value.toFixed(2);
  }
  return String(value);
};

// Excel export (CSV with better formatting)
export const exportToExcel = (report, data, layout) => {
  try {
    // Generate CSV with formatting hints
    let csv = '';
    
    // Add title
    csv += `"${report.name}"\n`;
    if (report.description) {
      csv += `"${report.description}"\n`;
    }
    csv += '\n';
    
    // Add data table
    if (data && data.data && data.data.length > 0) {
      const headers = Object.keys(data.data[0]);
      
      // Headers
      csv += headers.map(h => `"${h}"`).join(',') + '\n';
      
      // Rows
      data.data.forEach(row => {
        csv += headers.map(h => {
          const val = row[h];
          if (val === null || val === undefined) return '""';
          if (typeof val === 'number') return val;
          return `"${String(val).replace(/"/g, '""')}"`;
        }).join(',') + '\n';
      });
    }
    
    // Add metadata
    csv += '\n';
    csv += `"Generated","${new Date().toLocaleString()}"\n`;
    csv += `"Rows","${data.rowCount || 0}"\n`;
    
    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${report.name.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return true;
  } catch (error) {
    console.error('Error generating Excel:', error);
    throw error;
  }
};
